import { IconLayer } from '@deck.gl/layers';
import type { LayerState, LayerFeature, BusProps } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { GLIDE_MS, SNAP_METERS, JITTER_METERS } from './busMotion.js';
import { createGlideTracker } from './glideTracker.js';
import { createBusShapeSnapper } from './busShapeSnap.js';

const busProps = (f: LayerFeature): BusProps => f.props as unknown as BusProps;

// Rendered pill height on screen; everything else scales off this.
const BADGE_HEIGHT = 18; // px
// Draw the atlas at a higher resolution than it renders so the baked text stays
// crisp on hi-dpi displays and when the camera tilts.
const ATLAS_SCALE = 2;
const FONT_STACK =
  '700 {size}px -apple-system, "Helvetica Neue", Helvetica, Arial, sans-serif';

type IconDef = {
  id: string;
  url: string;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  mask: false;
};

// Shared offscreen context purely for measuring text width before we size a pill.
let measureCtx: CanvasRenderingContext2D | null = null;
function measure(text: string, font: string): number {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  const ctx = measureCtx!;
  ctx.font = font;
  return ctx.measureText(text).width;
}

// Perceived luminance (0–255) of an RGB triple, used to flip the label between
// dark and light so it stays legible on any route color.
function luminance([r, g, b]: readonly [number, number, number]): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// One baked badge per route label. NYC has on the order of a few hundred distinct
// routes, so this map settles quickly and we never re-rasterize a pill per frame.
const badgeCache = new Map<string, IconDef>();

// A route-bullet badge: a rounded pill filled with the route color and the route
// label baked in (MTA-style), so the bus number reads at a glance. Cached by label.
function badgeForRoute(line: string, color: readonly [number, number, number]): IconDef {
  const label = (line || '?').trim() || '?';
  const cached = badgeCache.get(label);
  if (cached) return cached;

  const H = BADGE_HEIGHT * ATLAS_SCALE;
  const fontSize = Math.round(H * 0.56);
  const font = FONT_STACK.replace('{size}', String(fontSize));
  const padX = Math.round(H * 0.42);
  const radius = H / 2;
  const border = Math.max(1, Math.round(ATLAS_SCALE)); // px, dark outline for contrast
  const textW = measure(label, font);
  const W = Math.ceil(textW + padX * 2);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // Pill body in the route color, with a thin dark outline so light-colored
  // routes (yellow/white) stay defined against the map.
  const inset = border / 2;
  ctx.beginPath();
  ctx.roundRect(inset, inset, W - border, H - border, radius);
  ctx.fillStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
  ctx.fill();
  ctx.lineWidth = border;
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.stroke();

  // Label, color-flipped for legibility against the pill.
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = luminance(color) > 150 ? '#111' : '#fff';
  ctx.fillText(label, W / 2, H / 2 + H * 0.04);

  const def: IconDef = {
    id: label,
    url: canvas.toDataURL(),
    width: W,
    height: H,
    anchorX: W / 2,
    anchorY: H / 2,
    mask: false,
  };
  badgeCache.set(label, def);
  return def;
}

interface BusDatum {
  position: [number, number];
  color: [number, number, number];
  line: string;
  vehicleId?: string;
  timestamp?: number;
  destination?: string;
  nextStop?: string;
}

const tracker = createGlideTracker({
  glideMs: GLIDE_MS,
  snapMeters: SNAP_METERS,
  jitterMeters: JITTER_METERS,
});
// Snaps buses onto their route shape so they follow the road; falls back to the
// straight-line glide above for any bus without a usable shape this frame.
const snapper = createBusShapeSnapper();

/**
 * Frame-driven bus layer: glides each marker from its current spot to the latest
 * reported position (entity interpolation), so buses roll continuously and
 * corrections slide in instead of teleporting. Returns null when state is absent,
 * down, or empty.
 */
export function buildBusesLayer(state: LayerState, _nowSec: number): IconLayer<BusDatum> | null {
  if (!state || state.status === 'down' || state.features.length === 0) return null;

  const nowMs = typeof performance !== 'undefined' ? performance.now() : 0;
  // One frame array feeds both the straight-line tracker (which reads id/position)
  // and the shape snapper (which also reads routeId/dir) — no second allocation.
  const frame = state.features.map((f) => {
    const p = busProps(f);
    return {
      id: f.id,
      position: (f.geometry as { coordinates: [number, number] }).coordinates,
      routeId: p.line,
      dir: p.dir,
      // Previous reported position from the server: seeds the straight-line glide so
      // a just-connected client starts moving on the first frame instead of waiting
      // two polls to observe a from→to segment (mirrors ferryLayer.ts).
      seedFrom: p.prev,
    };
  });
  const samples = tracker.step(frame, state.generatedAt, nowMs);

  // The snapper seeds from the live glide position (already computed this frame)
  // rather than the raw server prev, so that when a route shape finishes loading
  // mid-cycle the snapper picks up exactly where the straight-line glide shows the
  // bus — no backward jump to a position from a previous poll.
  const snapFrame = state.features.map((f) => {
    const p = busProps(f);
    return {
      id: f.id,
      position: (f.geometry as { coordinates: [number, number] }).coordinates,
      routeId: p.line,
      dir: p.dir,
      seedFrom: samples.get(f.id)?.position,
    };
  });
  const snapped = snapper.step(snapFrame, state.generatedAt, nowMs);

  const data: BusDatum[] = state.features.map((f) => {
    const p = busProps(f);
    return {
      // Road-snapped position when the route shape is available; otherwise the
      // straight-line glide.
      position: snapped.get(f.id) ?? samples.get(f.id)!.position,
      color: p.color,
      line: p.line,
      vehicleId: p.vehicleId,
      timestamp: p.timestamp,
      destination: p.destination,
      nextStop: p.nextStop,
    };
  });

  return new IconLayer<BusDatum>({
    id: 'buses',
    data,
    // Dynamic per-route packing: deck.gl atlases each unique badge once, keyed by
    // its id (the route label), so the pill is rasterized only when first seen.
    getIcon: (d) => badgeForRoute(d.line, d.color),
    getPosition: (d) => d.position,
    getSize: BADGE_HEIGHT,
    sizeUnits: 'pixels',
    billboard: true,
    pickable: true,
  });
}

export const busesRenderer: RendererDef = {
  layerId: 'buses',
  build: (state, nowSec) => buildBusesLayer(state, nowSec),
};
